import styled from 'styled-components/macro';

export const CartButton = styled.button`
  border: 0;
  padding: 0;
  width: 56px;
  height: 56px;
  color: #fff;
  background-color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  line-height: 56px;
  position: absolute;
  top: 18px;
  left: 0;
  cursor: pointer;
  z-index: 2;
  border-radius: 10px 0 0 10px;
  box-shadow: 0 10px 24px rgba(12, 11, 16, 0.24);

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 3px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }

  span {
    font-size: 1.6rem;
    line-height: 1;
  }
`;

interface IContainer {
  isOpen: boolean;
}
export const Container = styled.div<IContainer>`
  position: fixed;
  top: 12px;
  right: ${({ isOpen }) => (isOpen ? '0' : '-100%')};
  width: 100%;
  height: calc(100% - 24px);
  background: #fff;
  box-sizing: border-box;
  z-index: 99;
  border-radius: ${({ isOpen }) => (isOpen ? '18px 0 0 18px' : '0')};
  box-shadow: ${({ isOpen }) =>
    isOpen ? '-18px 0 42px rgba(12, 11, 16, 0.28)' : 'none'};
  overflow: ${({ isOpen }) => (isOpen ? 'hidden' : 'visible')};

  transition: right 0.2s;

  ${CartButton} {
    left: ${({ isOpen }) => (isOpen ? '18px' : '-56px')};
    background-color: ${({ theme, isOpen }) =>
      isOpen ? theme.colors.primary : theme.colors.primary};
  }

  @media only screen and (min-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    width: 460px;
    right: ${({ isOpen }) => (isOpen ? '12px' : '-460px')};

    ${CartButton} {
      left: ${({ isOpen }) => (isOpen ? '18px' : '-56px')};
    }
  }
`;

interface ICartIcon {
  large?: boolean;
  inverted?: boolean;
}
export const CartIcon = styled.div<ICartIcon>`
  width: ${({ large }) => (large ? '60px' : '50px')};
  height: ${({ large }) => (large ? '60px' : '50px')};
  position: relative;
  display: inline-block;
  vertical-align: middle;
  margin-right: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 14px;
    top: 17px;
    width: 24px;
    height: 16px;
    border: 2px solid
      ${({ theme, inverted }) => (inverted ? '#fff' : theme.colors.primary)};
    border-top: 0;
  }

  &::after {
    content: '';
    position: absolute;
    left: 18px;
    top: 36px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ theme, inverted }) =>
      inverted ? '#fff' : theme.colors.primary};
    box-shadow: 18px 0 0
      ${({ theme, inverted }) => (inverted ? '#fff' : theme.colors.primary)};
  }
`;

export const CartQuantity = styled.div`
  display: inline-block;
  width: 18px;
  height: 18px;
  color: #fff;
  font-weight: bold;
  font-size: 0.7em;
  text-align: center;
  line-height: 18px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
  position: absolute;
  bottom: 2px;
  right: 5px;
`;

export const CartContent = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 28px 28px 190px;
  box-sizing: border-box;

  /* MAC scrollbar para desktop*/
  @media screen and (min-width: 640px) {
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 10px;
      background-color: ${({ theme }) => theme.colors.accent};
      padding: 10px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: ${({ theme }) => theme.colors.border};
    }
  }
`;

export const CartContentHeader = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  box-sizing: border-box;
  padding: 48px 0 28px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const HeaderEyebrow = styled.p`
  margin: 0 0 18px;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0;
  text-transform: uppercase;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
`;

export const HeaderTitle = styled.span`
  font-weight: bold;
  font-size: 2rem;
  vertical-align: middle;
`;

export const Sub = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  font-weight: 700;
  text-transform: uppercase;
`;

export const SubPrice = styled.div`
  text-align: right;
  color: ${({ theme }) => theme.colors.muted};
`;

export const SubPriceValue = styled.p`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
`;

export const SubPriceInstallment = styled.p`
  margin: 0;
  font-size: 0.75rem;
`;

export const RestaurantField = styled.div`
  margin-bottom: 18px;
`;

export const RestaurantLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  margin-bottom: 8px;
`;

export const RestaurantInput = styled.input`
  width: 100%;
  height: 44px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  padding: 0 12px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
  }

  &:focus {
    outline: 3px solid rgba(238, 39, 55, 0.2);
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;

export const OrderNoteInput = styled.textarea`
  width: 100%;
  min-height: 96px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.35;
  padding: 12px;
  resize: vertical;

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
  }

  &:focus {
    outline: 3px solid rgba(238, 39, 55, 0.2);
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;

export const CheckoutButton = styled.button`
  width: 100%;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  color: #fff;
  text-transform: uppercase;
  background-color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  padding: 16px 0;
  margin-top: 18px;
  cursor: pointer;
  outline: none;
  font-size: 0.95rem;
  font-weight: 700;

  transition: background-color 0.2s, border-color 0.2s;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 3px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;

export const CartFooter = styled.div`
  box-sizing: border-box;
  padding: 22px 28px 24px;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 2;
  left: 0;
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 -18px 34px rgba(45, 47, 143, 0.12);

  > ${Sub}, > ${SubPrice} {
    display: inline-block;
    vertical-align: top;
  }

  > ${Sub} {
    width: 34%;
  }

  > ${SubPrice} {
    width: 66%;
  }

  &::before {
    display: none;
  }
`;

export const CheckoutForm = styled.div`
  padding: 28px 0 0;
`;

export const CheckoutTitle = styled.h3`
  margin: 0 0 18px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
`;

export const StepBackButton = styled.button`
  border: 0;
  padding: 0;
  margin: 0 0 20px;
  color: ${({ theme }) => theme.colors.primary};
  background: transparent;
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &::before {
    content: '<';
    margin-right: 8px;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 3px;
  }
`;
